import type { FC, ReactNode } from 'react'

import { Check, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import NeonLink from '@/components/landing/neon-link'
import SectionHeading from '@/components/landing/section-heading'
import { PLANS } from '@/constants/landing'

const PricingSection: FC = (): ReactNode => {
    return (
        <section id='pricing' aria-labelledby='pricing-title' className='relative scroll-mt-20 py-24 sm:py-32'>
            <div aria-hidden='true' className='pointer-events-none absolute inset-0 -z-10 overflow-hidden'>
                <div className='absolute left-1/2 top-1/2 size-[36rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-neon-cyan/10 blur-[140px]' />
            </div>

            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <SectionHeading
                    id='pricing-title'
                    eyebrow='Pricing'
                    align='center'
                    title='Free to play. Forever.'
                    description="Every game, every arena and the daily puzzle cost nothing. Neon+ is there when you want to train harder."
                />

                <ul className='mx-auto mt-14 grid max-w-4xl gap-5 md:grid-cols-2'>
                    {PLANS.map(plan => (
                        <li
                            key={plan.name}
                            className={cn(
                                'relative flex flex-col rounded-3xl border p-7 sm:p-8',
                                plan.highlighted
                                    ? 'border-neon-cyan/55 bg-[#07141a]/80 shadow-[0_0_0_1px_rgb(46_242_255/0.2),0_0_50px_rgb(46_242_255/0.18)]'
                                    : 'border-white/10 bg-card/60'
                            )}
                        >
                            <div className='flex items-center justify-between gap-3'>
                                <h3 className={cn('font-heading text-xl font-semibold tracking-tight', plan.highlighted && 'text-neon-cyan glow-text-cyan')}>
                                    {plan.name}
                                </h3>

                                {plan.badge && (
                                    <span className='inline-flex items-center gap-1.5 rounded-full border border-neon-pink/45 bg-neon-pink/10 px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-neon-pink'>
                                        <Sparkles className='size-3' />
                                        {plan.badge}
                                    </span>
                                )}
                            </div>

                            <p className='mt-6 flex items-baseline gap-2'>
                                <span className='font-heading text-5xl font-semibold tracking-tight'>
                                    {plan.price}
                                </span>

                                <span className='text-sm text-muted-foreground'>
                                    {plan.period}
                                </span>
                            </p>

                            <p className='mt-3 text-sm leading-relaxed text-muted-foreground'>
                                {plan.description}
                            </p>

                            <ul className='mt-7 flex-1 space-y-3 border-t border-white/5 pt-7'>
                                {plan.features.map(feature => (
                                    <li key={feature} className='flex items-start gap-3 text-sm text-foreground/90'>
                                        <Check className={cn('mt-0.5 size-4 shrink-0', plan.highlighted ? 'text-neon-cyan' : 'text-neon-lime')} />
                                        {feature}
                                    </li>
                                ))}
                            </ul>

                            <NeonLink href='#play' variant={plan.highlighted ? 'primary' : 'outline'} className='mt-8 w-full'>
                                Start playing
                            </NeonLink>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    )
}

export default PricingSection
